"use client";

import {
  createContext,
  use,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

const ReadableCopyContext = createContext<ReactNode>(null);

// Stencil adds this class to a component once it, and every component inside
// it, has rendered: only then has it reached its final size.
const STENCIL_RENDERED_CLASS = "hydrated";

// Settled once the server placeholder has been replaced and every Stencil
// component has rendered. Placeholders are the only visible
// [data-client-pending] elements; settle markers are hidden.
function hasSettled(scope: Element) {
  if (scope.querySelector("[data-client-pending]:not([hidden])")) return false;
  for (const element of scope.getElementsByTagName("*")) {
    if (
      element.localName.startsWith("isov-") &&
      !element.classList.contains(STENCIL_RENDERED_CLASS)
    ) {
      return false;
    }
  }
  return true;
}

type ReadableCopyProps = {
  copy: ReactNode;
  children: ReactNode;
};

export function ReadableCopy({ copy, children }: ReadableCopyProps) {
  const markerRef = useRef<HTMLSpanElement>(null);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    const scope = markerRef.current?.parentElement;
    let frame = 0;

    const check = () => {
      frame = 0;
      // Only content in <main> waits for a section (see globals.scss).
      if (!scope?.closest("main") || hasSettled(scope)) {
        observer.disconnect();
        setSettled(true);
      }
    };

    const observer = new MutationObserver(() => {
      frame ||= requestAnimationFrame(check);
    });
    if (scope) {
      observer.observe(scope, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      });
    }
    frame = requestAnimationFrame(check);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <ReadableCopyContext value={copy}>
      {children}
      {/* data-client-pending: Stencil components render frames after React
          mounts them, so until they have, globals.scss keeps what follows
          this section in <main> invisible and their resize moves nothing. */}
      {!settled && <span ref={markerRef} hidden data-client-pending="" />}
    </ReadableCopyContext>
  );
}

export function readableFallback(placeholderClassName?: string) {
  return function ReadableFallback() {
    const copy = use(ReadableCopyContext);

    return (
      <>
        {/* data-client-pending: globals.scss keeps what follows it in <main>
            invisible until this section arrives, so nothing visibly shifts. */}
        {placeholderClassName && (
          <div
            className={placeholderClassName}
            aria-hidden="true"
            data-client-pending=""
          />
        )}
        {/*
         * React throws away a client-only boundary's server fallback without
         * hydrating it, so the copy only exists in the server HTML, for clients
         * that read it without running JavaScript (AI agents, crawlers).
         * Browsers never render or hydrate it.
         */}
        {typeof window === "undefined" && copy && (
          <div className="sr-only" inert>
            {copy}
          </div>
        )}
      </>
    );
  };
}
